// src/components/Header.jsx
import React from "react";
import { Box, Button, Avatar, Menu, MenuItem, useTheme, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useThemeSettings } from "../context/ThemeContext";
import ModelSelectorButton from "./ModelSelectorButton";
import LogoutIcon from "@mui/icons-material/Logout";

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const theme = useTheme();
  const { mode } = useThemeSettings();
  const isDark = mode === "dark";

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    // Ensure redirect to home page after logout
    setTimeout(() => {
      navigate("/", { replace: true });
    }, 0);
  };

  const handleSettings = () => {
    navigate("/settings");
    handleMenuClose();
  };

  // Check if user is on free plan
  const isFreePlan = !user?.subscription_plan || user?.subscription_plan?.toLowerCase() === "free";

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        background: "#ffffff",
        borderBottom: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 24px",
        height: "64px",
        boxSizing: "border-box",
      }}
    >
      {/* Model Selector on the LEFT */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <ModelSelectorButton />
      </Box>

      {/* Expanding space in the middle */}
      <Box sx={{ flex: 1 }} />

      {/* User Menu on the RIGHT */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <h1
          style={{
            color: "#1e3a8a",
            fontSize: "1.25rem",
            fontWeight: 800,
            letterSpacing: "0.5px",
            margin: 0,
            flexShrink: 0,
          }}
        >
          exivis
        </h1>
        {user && (
          <>
            <Button
              onClick={handleMenuOpen}
              sx={{
                color: "#0f172a",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 600,
                fontSize: "0.9rem",
                borderRadius: "8px",
                "&:hover": {
                  bgcolor: "rgba(30, 58, 138, 0.05)",
                }
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "#1e3a8a",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                {(() => {
                  const name = user?.name;
                  if (typeof name === "string" && name.length > 0) {
                    return name.charAt(0).toUpperCase();
                  }
                  return "U";
                })()}
              </Avatar>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                {(() => {
                  const name = user?.name;
                  const email = user?.email;
                  if (typeof name === "string" && name.length > 0) {
                    return name;
                  }
                  if (typeof email === "string" && email.length > 0) {
                    return email;
                  }
                  return "User";
                })()}
              </Box>
              {isFreePlan && (
                <Chip
                  label="Free"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: "#f1f5f9",
                    color: "#64748b",
                    ml: 0.5,
                    borderRadius: "4px",
                  }}
                />
              )}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{
                "& .MuiPaper-root": {
                  bgcolor: "#ffffff",
                  color: "#0f172a",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  minWidth: 180,
                  mt: 1,
                },
              }}
            >
              <MenuItem onClick={handleSettings} sx={{ py: 1.2, fontWeight: 500 }}>
                Settings
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ py: 1.2, fontWeight: 500, color: "#ef4444" }}>
                <LogoutIcon sx={{ mr: 1.5, fontSize: "20px" }} />
                Logout
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </header>
  );
}
